import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle, CheckCircle, Phone, Mail, FileText, Download, Copy,
  ChevronDown, ChevronUp, Clock, ExternalLink, Shield, Users, Briefcase,
  HardHat, Scale, RefreshCw, Send, Check, X, Activity,
} from 'lucide-react'
import api from '../services/api'
import { useAuthStore } from '../stores/authStore'

// ── Regulator data ────────────────────────────────────────────────────────────
const REGULATORS: Record<string, { name: string; phone: string; url: string; email?: string }> = {
  NSW: { name: 'SafeWork NSW', phone: '13 10 50', url: 'https://www.safework.nsw.gov.au/notify', email: 'notifications@safework.nsw.gov.au' },
  VIC: { name: 'WorkSafe VIC', phone: '1800 136 089', url: 'https://www.worksafe.vic.gov.au/report' },
  QLD: { name: 'WHSQ', phone: '1300 362 128', url: 'https://www.worksafe.qld.gov.au' },
  WA:  { name: 'WorkSafe WA', phone: '1300 307 877', url: 'https://www.worksafe.wa.gov.au' },
  SA:  { name: 'SafeWork SA', phone: '1300 365 255', url: 'https://www.safework.sa.gov.au' },
  TAS: { name: 'WorkSafe TAS', phone: '1300 366 322', url: 'https://www.worksafe.tas.gov.au' },
  ACT: { name: 'WorkSafe ACT', phone: '(02) 6207 3000', url: 'https://www.worksafe.act.gov.au' },
  NT:  { name: 'NT WorkSafe', phone: '1800 019 115', url: 'https://www.worksafe.nt.gov.au' },
}

const WC_INSURERS: Record<string, { name: string; phone: string }> = {
  NSW: { name: 'icare NSW', phone: '13 77 22' },
  VIC: { name: 'WorkSafe VIC', phone: '1800 136 089' },
  QLD: { name: 'WorkCover QLD', phone: '1300 362 128' },
  WA:  { name: 'WorkCover WA', phone: '1300 794 744' },
  SA:  { name: 'ReturnToWorkSA', phone: '13 18 55' },
  TAS: { name: 'WorkSafe TAS', phone: '1300 366 322' },
  ACT: { name: 'WorkSafe ACT', phone: '(02) 6207 3000' },
  NT:  { name: 'NT WorkSafe', phone: '1800 019 115' },
}

const ELT_ROLES = [
  { key: 'ceo', label: 'CEO / Managing Director' },
  { key: 'coo', label: 'Chief Operating Officer' },
  { key: 'safety_manager', label: 'Corporate Safety Manager' },
  { key: 'legal', label: 'Legal Counsel', note: 'Verbal briefing recommended — do not discuss details via SMS' },
  { key: 'hr', label: 'HR Manager' },
  { key: 'insurance', label: 'Insurance Broker', note: 'Most policies require notification within 24 hours' },
]

// ── Countdown timer hook ──────────────────────────────────────────────────────
function useCountdown(dueDate: string | null, completedAt: string | null) {
  const [secs, setSecs] = useState<number | null>(null)
  const [overdue, setOverdue] = useState(false)

  useEffect(() => {
    if (!dueDate || completedAt) { setSecs(null); setOverdue(false); return }
    const tick = () => {
      const rem = Math.floor((new Date(dueDate).getTime() - Date.now()) / 1000)
      if (rem <= 0) { setSecs(0); setOverdue(true) } else { setSecs(rem); setOverdue(false) }
    }
    tick()
    const t = setInterval(tick, 1000)
    return () => clearInterval(t)
  }, [dueDate, completedAt])

  return { secs, overdue }
}

function formatCountdown(secs: number | null): string {
  if (secs === null) return ''
  if (secs <= 0) return 'OVERDUE'
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = secs % 60
  if (h > 0) return `${h}h ${m}m`
  return `${m}m ${s}s`
}

function timerColour(secs: number | null, totalSecs: number): string {
  if (secs === null || secs <= 0) return 'var(--red)'
  const pct = secs / totalSecs
  if (pct > 0.5) return '#00875A'
  if (pct > 0.25) return '#FF8B00'
  return 'var(--red)'
}

// ── Section wrapper ───────────────────────────────────────────────────────────
interface SectionProps {
  title: string
  icon: React.ReactNode
  complete?: boolean
  urgent?: boolean
  defaultOpen?: boolean
  children: React.ReactNode
  badgeCount?: number
}

function Section({ title, icon, complete, urgent, defaultOpen = true, children, badgeCount }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        border: `1px solid ${urgent ? 'var(--red)' : complete ? '#00875A' : 'var(--border)'}`,
        background: urgent ? 'rgba(222,53,11,0.03)' : 'white',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
        style={{ background: complete ? 'rgba(0,135,90,0.06)' : urgent ? 'rgba(222,53,11,0.06)' : 'var(--bg)' }}
      >
        <span style={{ color: urgent ? 'var(--red)' : complete ? '#00875A' : 'var(--accent)', flexShrink: 0 }}>
          {complete ? <CheckCircle size={18} /> : icon}
        </span>
        <span className="font-semibold text-sm flex-1" style={{ color: urgent ? 'var(--red)' : complete ? '#00875A' : 'var(--text)' }}>
          {title}
        </span>
        {badgeCount !== undefined && badgeCount > 0 && (
          <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
            style={{ background: urgent ? 'var(--red)' : 'var(--accent)', color: 'white' }}>
            {badgeCount}
          </span>
        )}
        {complete && <span className="text-xs font-semibold" style={{ color: '#00875A' }}>Complete</span>}
        {open ? <ChevronUp size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
               : <ChevronDown size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
      </button>
      {open && <div className="px-4 pb-4 pt-2">{children}</div>}
    </div>
  )
}

// ── Props ─────────────────────────────────────────────────────────────────────
interface NotificationActionPanelProps {
  incident: any
  onUpdate: () => void
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function NotificationActionPanel({ incident, onUpdate }: NotificationActionPanelProps) {
  const { user } = useAuthStore()
  const navigate = useNavigate()

  const [complianceData, setComplianceData] = useState<{
    actions: any[]; contracts: any[]; contacts: any[]; investigation: any | null;
  }>({ actions: [], contracts: [], contacts: [], investigation: null })
  const [loading, setLoading] = useState(true)

  // Section states
  const [noticeModal, setNoticeModal] = useState(false)
  const [noticeDraft, setNoticeDraft] = useState('')
  const [noticeLoading, setNoticeLoading] = useState(false)
  const [clientNoticeModal, setClientNoticeModal] = useState<{ open: boolean; contractId?: string; draft?: string }>({ open: false })
  const [clientNoticeLoading, setClientNoticeLoading] = useState(false)
  const [eltSending, setEltSending] = useState<Record<string, boolean>>({})
  const [copiedNotice, setCopiedNotice] = useState(false)
  const [copiedClient, setCopiedClient] = useState(false)
  const [actionError, setActionError] = useState('')

  const jurisdiction = incident.jurisdiction || 'NSW'
  const regulator = REGULATORS[jurisdiction] || REGULATORS.NSW
  const wcInsurer = WC_INSURERS[jurisdiction] || WC_INSURERS.NSW

  // Countdown timers
  const phone = useCountdown(incident.notification_phone_due, incident.notification_phone_completed)
  const written = useCountdown(incident.notification_written_due, incident.notification_written_completed)

  const loadCompliance = useCallback(async () => {
    try {
      const { data } = await api.get(`/incidents/${incident.id}/compliance`)
      setComplianceData(data)
    } catch {}
    setLoading(false)
  }, [incident.id])

  useEffect(() => { loadCompliance() }, [loadCompliance])

  // Helper: check if a specific action_type is done
  const isDone = (actionType: string, contactType?: string) =>
    complianceData.actions.some(a =>
      a.action_type === actionType && (!contactType || a.contact_type === contactType)
    )

  const getAction = (actionType: string, contactType?: string) =>
    complianceData.actions.find(a =>
      a.action_type === actionType && (!contactType || a.contact_type === contactType)
    )

  const logAction = async (action_type: string, contact_type?: string, method?: string, notes?: string) => {
    try {
      await api.post(`/incidents/${incident.id}/compliance`, { action_type, contact_type, method, notes })
      await loadCompliance()
      onUpdate()
    } catch (err: any) {
      setActionError(err.response?.data?.error || 'Action failed')
      setTimeout(() => setActionError(''), 3000)
    }
  }

  const sendElt = async (contact: any, method: 'sms' | 'email') => {
    const key = `${contact.id}-${method}`
    setEltSending(s => ({ ...s, [key]: true }))
    try {
      await api.post(`/incidents/${incident.id}/notify-elt`, {
        contactId: contact.id,
        contactType: contact.role,
        method,
      })
      await loadCompliance()
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Send failed')
      setTimeout(() => setActionError(''), 4000)
    }
    setEltSending(s => ({ ...s, [key]: false }))
  }

  const generateNotice = async () => {
    setNoticeLoading(true)
    try {
      const { data } = await api.post(`/incidents/${incident.id}/generate-notice`, {})
      setNoticeDraft(data.notice)
      setNoticeModal(true)
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Generation failed')
      setTimeout(() => setActionError(''), 4000)
    }
    setNoticeLoading(false)
  }

  const generateClientNotice = async (contractId?: string) => {
    setClientNoticeLoading(true)
    setClientNoticeModal({ open: true, contractId, draft: undefined })
    try {
      const { data } = await api.post(`/incidents/${incident.id}/generate-client-notice`, { contractId })
      setClientNoticeModal(prev => ({ ...prev, draft: data.draft }))
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Generation failed')
      setClientNoticeModal({ open: false })
      setTimeout(() => setActionError(''), 4000)
    }
    setClientNoticeLoading(false)
  }

  const downloadNotice = () => {
    const blob = new Blob([noticeDraft], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${incident.incident_number}-s38-notice.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  const downloadComplianceReport = () => {
    const lines: string[] = [
      `COMPLIANCE ACTIVITY REPORT`,
      `Incident: ${incident.incident_number}`,
      `Date Occurred: ${new Date(incident.date_occurred).toLocaleString('en-AU')}`,
      `Site: ${incident.site_name || 'Unknown'} (${jurisdiction})`,
      `Generated: ${new Date().toLocaleString('en-AU')}`,
      ``,
      `═══════════════════════════════════════`,
      `COMPLIANCE ACTIONS LOG`,
      `═══════════════════════════════════════`,
    ]
    complianceData.actions.forEach(a => {
      lines.push(`[${new Date(a.created_at).toLocaleString('en-AU')}] ${a.action_type}${a.contact_type ? ' — ' + a.contact_type : ''} (${a.method || 'manual'}) — ${a.completed_by_name || 'Unknown'}`)
      if (a.notes) lines.push(`  Notes: ${a.notes}`)
    })
    lines.push('')
    lines.push('AI-assisted compliance tracking — HSEQ Intelligence Platform')

    const blob = new Blob([lines.join('\n')], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${incident.incident_number}-compliance-report.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── Compliance progress calculation ──────────────────────────────────────────
  const progress = {
    regulatory: {
      phone: isDone('phone_notification'),
      written: isDone('written_notification'),
      total: 2,
      done: [isDone('phone_notification'), isDone('written_notification')].filter(Boolean).length,
    },
    elt: {
      total: ELT_ROLES.length,
      done: ELT_ROLES.filter(r => isDone('elt_notified', r.key)).length,
    },
    scene: {
      preserved: isDone('scene_preserved'),
      clearance: isDone('scene_clearance'),
      total: 2,
      done: [isDone('scene_preserved'), isDone('scene_clearance')].filter(Boolean).length,
    },
    wc: incident.injury_type ? {
      insurer: isDone('wc_insurer'),
      claim: isDone('wc_claim'),
      worker: isDone('worker_informed'),
      total: 3,
      done: [isDone('wc_insurer'), isDone('wc_claim'), isDone('worker_informed')].filter(Boolean).length,
    } : null,
    client: {
      total: complianceData.contracts.length,
      done: complianceData.contracts.filter(c => isDone('client_notified', c.id)).length,
    },
  }

  const allDone = [
    progress.regulatory.phone, progress.regulatory.written,
    progress.scene.preserved, progress.scene.clearance,
    ...ELT_ROLES.map(r => isDone('elt_notified', r.key)),
    ...(progress.wc ? [progress.wc.insurer, progress.wc.claim, progress.wc.worker] : []),
  ]
  const totalItems = allDone.length
  const doneItems = allDone.filter(Boolean).length
  const overallPct = totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 0

  // Urgent items: overdue or <25% remaining
  const urgentItems: string[] = []
  if (!progress.regulatory.phone) {
    if (phone.overdue) urgentItems.push('Phone notification to regulator is OVERDUE')
    else if (phone.secs !== null && incident.notification_phone_due) {
      const pct = phone.secs / 3600
      if (pct < 0.25) urgentItems.push(`Phone notification due in ${formatCountdown(phone.secs)}`)
    }
  }
  if (!progress.regulatory.written) {
    if (written.overdue) urgentItems.push('Written notification to regulator is OVERDUE')
    else if (written.secs !== null && incident.notification_written_due) {
      const pct = written.secs / 172800
      if (pct < 0.25) urgentItems.push(`Written notification due in ${formatCountdown(written.secs)}`)
    }
  }
  if (!progress.scene.preserved) urgentItems.push('Scene preservation not confirmed')

  const PhoneTimer = () => {
    const isPulsing = phone.secs !== null && phone.secs <= 900 && !progress.regulatory.phone
    const colour = progress.regulatory.phone ? '#00875A' : timerColour(phone.secs, 3600)
    return (
      <div className={`text-lg font-bold tabular-nums ${isPulsing ? 'pulse-red' : ''}`} style={{ color: colour }}>
        {progress.regulatory.phone ? '✓ Complete' : phone.overdue ? 'OVERDUE' : formatCountdown(phone.secs)}
      </div>
    )
  }

  const WrittenTimer = () => {
    const isPulsing = written.secs !== null && written.secs <= 900 && !progress.regulatory.written
    const colour = progress.regulatory.written ? '#00875A' : timerColour(written.secs, 172800)
    return (
      <div className={`text-lg font-bold tabular-nums ${isPulsing ? 'pulse-red' : ''}`} style={{ color: colour }}>
        {progress.regulatory.written ? '✓ Complete' : written.overdue ? 'OVERDUE' : formatCountdown(written.secs)}
      </div>
    )
  }

  if (loading) return (
    <div className="card animate-pulse" style={{ color: 'var(--text-muted)' }}>
      Loading compliance panel...
    </div>
  )

  return (
    <div className="space-y-4">
      <style>{`
        @keyframes pulse-red { 0%,100% { opacity:1 } 50% { opacity:0.4 } }
        .pulse-red { animation: pulse-red 1s ease-in-out infinite }
      `}</style>

      {/* ── Urgent banner ── */}
      {urgentItems.length > 0 && (
        <div className="rounded-xl p-4 space-y-2" style={{ background: 'var(--red)', color: 'white' }}>
          <div className="flex items-center gap-2 font-bold text-sm">
            <AlertTriangle size={18} />
            URGENT — ACTION REQUIRED NOW
          </div>
          {urgentItems.map((item, i) => (
            <div key={i} className="text-sm font-medium">• {item}</div>
          ))}
        </div>
      )}

      {/* ── Master Compliance Tracker ── */}
      <div className="card space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Shield size={20} style={{ color: 'var(--accent)' }} />
            <h2 className="font-bold text-base" style={{ color: 'var(--text)' }}>
              Compliance Tracker — {incident.incident_number}
            </h2>
          </div>
          <div className="text-sm font-bold" style={{ color: overallPct === 100 ? '#00875A' : overallPct >= 50 ? '#FF8B00' : 'var(--red)' }}>
            {overallPct}% Complete
          </div>
        </div>

        {/* Overall progress bar */}
        <div className="w-full rounded-full h-2" style={{ background: 'var(--border)' }}>
          <div
            className="h-2 rounded-full transition-all duration-500"
            style={{ width: `${overallPct}%`, background: overallPct === 100 ? '#00875A' : overallPct >= 50 ? '#FF8B00' : 'var(--red)' }}
          />
        </div>

        {/* Category progress */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Regulatory', done: progress.regulatory.done, total: progress.regulatory.total },
            { label: 'ELT Internal', done: progress.elt.done, total: progress.elt.total },
            { label: 'Scene', done: progress.scene.done, total: progress.scene.total },
            ...(progress.wc ? [{ label: 'Workers Comp', done: progress.wc.done, total: progress.wc.total }] : []),
            ...(progress.client.total > 0 ? [{ label: 'Clients', done: progress.client.done, total: progress.client.total }] : []),
          ].map(cat => {
            const pct = cat.total > 0 ? Math.round((cat.done / cat.total) * 100) : 0
            return (
              <div key={cat.label} className="rounded-lg p-2.5 space-y-1.5" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <div className="flex justify-between text-xs">
                  <span style={{ color: 'var(--text-secondary)' }} className="font-medium">{cat.label}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{cat.done}/{cat.total}</span>
                </div>
                <div className="w-full rounded-full h-1.5" style={{ background: 'var(--border)' }}>
                  <div
                    className="h-1.5 rounded-full"
                    style={{ width: `${pct}%`, background: pct === 100 ? '#00875A' : pct >= 50 ? '#FF8B00' : 'var(--red)' }}
                  />
                </div>
              </div>
            )
          })}
        </div>

        {actionError && (
          <div className="text-sm p-3 rounded-lg" style={{ background: 'rgba(222,53,11,0.1)', color: 'var(--red)' }}>
            {actionError}
          </div>
        )}
      </div>

      {/* ── Section 1: Scene Preservation ── */}
      <Section
        title="Scene Preservation"
        icon={<AlertTriangle size={18} />}
        complete={progress.scene.done === 2}
        urgent={!progress.scene.preserved}
        defaultOpen={!progress.scene.preserved}
      >
        <div className="space-y-3">
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            The incident scene must be preserved until a regulator gives clearance or investigation is complete.
          </p>
          {[
            { key: 'scene_preserved', label: 'Scene preserved and secured', actionType: 'scene_preserved' },
            { key: 'scene_clearance', label: 'SafeWork NSW has given clearance to proceed', actionType: 'scene_clearance' },
          ].map(item => {
            const done = isDone(item.actionType)
            const act = getAction(item.actionType)
            return (
              <label key={item.key} className="flex items-start gap-3 cursor-pointer">
                <div
                  className="flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5"
                  style={{ borderColor: done ? '#00875A' : 'var(--border)', background: done ? '#00875A' : 'white', cursor: 'pointer' }}
                  onClick={() => !done && logAction(item.actionType)}
                >
                  {done && <Check size={12} color="white" />}
                </div>
                <div>
                  <div className="text-sm font-medium" style={{ color: done ? '#00875A' : 'var(--text)' }}>{item.label}</div>
                  {done && act && (
                    <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                      {act.completed_by_name} — {new Date(act.created_at).toLocaleString('en-AU')}
                    </div>
                  )}
                </div>
              </label>
            )
          })}
        </div>
      </Section>

      {/* ── Section 2: Regulatory Notification ── */}
      <Section
        title={`Regulatory Notification — ${regulator.name}`}
        icon={<Phone size={18} />}
        complete={progress.regulatory.done === 2}
        urgent={phone.overdue || written.overdue}
        defaultOpen={progress.regulatory.done < 2}
      >
        <div className="space-y-4">
          {/* Regulator info */}
          <div className="flex flex-wrap gap-3 text-sm">
            <a href={`tel:${regulator.phone.replace(/\s/g, '')}`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold"
              style={{ background: 'var(--accent)', color: 'white', textDecoration: 'none' }}>
              <Phone size={14} /> {regulator.phone}
            </a>
            <a href={regulator.url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
              style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--accent)', textDecoration: 'none' }}>
              <ExternalLink size={14} /> Open Portal
            </a>
            {regulator.email && (
              <a href={`mailto:${regulator.email}`}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-secondary)', textDecoration: 'none' }}>
                <Mail size={14} /> {regulator.email}
              </a>
            )}
          </div>

          {/* Phone notification */}
          <div className="rounded-xl p-4 space-y-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                  Phone Notification (1 hour)
                </div>
                <PhoneTimer />
              </div>
              <Clock size={24} style={{ color: progress.regulatory.phone ? '#00875A' : timerColour(phone.secs, 3600) }} />
            </div>

            {!progress.regulatory.phone && (
              <>
                <div className="rounded-lg p-3 text-xs space-y-1" style={{ background: 'white', border: '1px solid var(--border)' }}>
                  <div className="font-semibold" style={{ color: 'var(--text-muted)' }}>WHAT TO SAY:</div>
                  <div style={{ color: 'var(--text)', lineHeight: 1.6 }}>
                    "My name is [your name] calling from {incident.site_name || 'our company'}.
                    I need to notify you of a notifiable incident that occurred at {incident.site_name || 'our site'}
                    {incident.jurisdiction ? ` in ${incident.jurisdiction}` : ''} on{' '}
                    {new Date(incident.date_occurred).toLocaleString('en-AU')}.
                    The incident reference is {incident.incident_number}.
                    {incident.injury_type ? ` There was a ${incident.injury_type}.` : ' There was an incident involving a dangerous occurrence.'}
                    The scene has been secured and the area is safe. Can I take a reference number for this notification?"
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => logAction('phone_notification', undefined, 'phone', `Phone notification to ${regulator.name}`)}
                  className="btn btn-danger text-sm"
                >
                  <Check size={14} className="inline mr-1.5" />
                  Mark Phone Call Complete
                </button>
              </>
            )}
            {progress.regulatory.phone && (() => {
              const act = getAction('phone_notification')
              return (
                <div className="flex items-center gap-2 text-sm" style={{ color: '#00875A' }}>
                  <CheckCircle size={16} />
                  Phone call completed by {act?.completed_by_name} at {act ? new Date(act.created_at).toLocaleString('en-AU') : ''}
                </div>
              )
            })()}
          </div>

          {/* Written notification */}
          <div className="rounded-xl p-4 space-y-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                  Written Notification (48 hours)
                </div>
                <WrittenTimer />
              </div>
              <FileText size={24} style={{ color: progress.regulatory.written ? '#00875A' : timerColour(written.secs, 172800) }} />
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={generateNotice}
                disabled={noticeLoading}
                className="btn btn-secondary text-sm flex items-center gap-2"
              >
                {noticeLoading ? <RefreshCw size={14} className="animate-spin" /> : <FileText size={14} />}
                {noticeLoading ? 'Generating...' : 'Generate Written Notice (AI)'}
              </button>
              {!progress.regulatory.written && (
                <button
                  type="button"
                  onClick={() => logAction('written_notification', undefined, 'portal', `Written notice submitted to ${regulator.name}`)}
                  className="btn btn-danger text-sm"
                >
                  <Check size={14} className="inline mr-1.5" />
                  Mark Submitted
                </button>
              )}
            </div>

            {progress.regulatory.written && (() => {
              const act = getAction('written_notification')
              return (
                <div className="flex items-center gap-2 text-sm" style={{ color: '#00875A' }}>
                  <CheckCircle size={16} />
                  Written notice submitted by {act?.completed_by_name} at {act ? new Date(act.created_at).toLocaleString('en-AU') : ''}
                </div>
              )
            })()}
          </div>
        </div>
      </Section>

      {/* ── Section 3: ELT Notifications ── */}
      <Section
        title="ELT Internal Notifications"
        icon={<Users size={18} />}
        complete={progress.elt.done === progress.elt.total && progress.elt.total > 0}
        badgeCount={progress.elt.total - progress.elt.done}
        defaultOpen={progress.elt.done < progress.elt.total}
      >
        <div className="space-y-3">
          {/* Notify all button */}
          {progress.elt.done < ELT_ROLES.length && complianceData.contacts.length > 0 && (
            <button
              type="button"
              onClick={async () => {
                for (const contact of complianceData.contacts) {
                  if (!isDone('elt_notified', contact.role)) {
                    if (contact.method_sms && contact.mobile) await sendElt(contact, 'sms')
                    else if (contact.method_email && contact.email) await sendElt(contact, 'email')
                  }
                }
              }}
              className="btn btn-primary text-sm flex items-center gap-2"
            >
              <Send size={14} /> Notify All Pending ELT Contacts
            </button>
          )}

          {ELT_ROLES.map(role => {
            const done = isDone('elt_notified', role.key)
            const act = getAction('elt_notified', role.key)
            // Find matching contact(s)
            const matchingContacts = complianceData.contacts.filter(c =>
              c.role?.toLowerCase().includes(role.key) ||
              (role.key === 'safety_manager' && (c.role?.toLowerCase().includes('safety') || c.role?.toLowerCase().includes('hse'))) ||
              (role.key === 'insurance' && c.role?.toLowerCase().includes('insur'))
            )
            const contact = matchingContacts[0]

            return (
              <div key={role.key} className="rounded-lg p-3 space-y-2"
                style={{ background: done ? 'rgba(0,135,90,0.05)' : 'var(--bg)', border: `1px solid ${done ? '#00875A40' : 'var(--border)'}` }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-semibold" style={{ color: done ? '#00875A' : 'var(--text)' }}>
                      {done ? <CheckCircle size={14} className="inline mr-1.5" style={{ color: '#00875A' }} /> : null}
                      {role.label}
                    </div>
                    {role.note && (
                      <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>{role.note}</div>
                    )}
                    {contact && (
                      <div className="text-xs mt-1 space-y-0.5" style={{ color: 'var(--text-secondary)' }}>
                        <div>{contact.name}</div>
                        {contact.mobile && <div><a href={`tel:${contact.mobile}`} style={{ color: 'var(--accent)' }}>{contact.mobile}</a></div>}
                        {contact.email && <div><a href={`mailto:${contact.email}`} style={{ color: 'var(--accent)' }}>{contact.email}</a></div>}
                      </div>
                    )}
                    {done && act && (
                      <div className="text-xs mt-1" style={{ color: '#00875A' }}>
                        Notified by {act.completed_by_name} at {new Date(act.created_at).toLocaleString('en-AU')}
                        {act.method && ` via ${act.method}`}
                      </div>
                    )}
                    {!contact && !done && (
                      <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                        No contact configured — add in Settings → Organisation → Notification Contacts
                      </div>
                    )}
                  </div>

                  {!done && (
                    <div className="flex flex-wrap gap-1.5 flex-shrink-0">
                      {contact?.mobile && contact?.method_sms && (
                        <button
                          type="button"
                          onClick={() => sendElt(contact, 'sms')}
                          disabled={eltSending[`${contact.id}-sms`]}
                          className="btn btn-secondary text-xs py-1"
                          style={{ fontSize: 11 }}
                        >
                          {eltSending[`${contact.id}-sms`] ? '...' : 'SMS'}
                        </button>
                      )}
                      {contact?.email && contact?.method_email && (
                        <button
                          type="button"
                          onClick={() => sendElt(contact, 'email')}
                          disabled={eltSending[`${contact.id}-email`]}
                          className="btn btn-secondary text-xs py-1"
                          style={{ fontSize: 11 }}
                        >
                          {eltSending[`${contact.id}-email`] ? '...' : 'Email'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => logAction('elt_notified', role.key, 'manual', `${role.label} manually notified`)}
                        className="btn text-xs py-1"
                        style={{ background: '#00875A', color: 'white', fontSize: 11 }}
                      >
                        Mark Done
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </Section>

      {/* ── Section 4: Client Notifications ── */}
      {complianceData.contracts.length > 0 && (
        <Section
          title="Client Notifications"
          icon={<Briefcase size={18} />}
          complete={progress.client.done === progress.client.total && progress.client.total > 0}
          badgeCount={progress.client.total - progress.client.done}
          defaultOpen={progress.client.done < progress.client.total}
        >
          <div className="space-y-3">
            {complianceData.contracts.map(contract => {
              const done = isDone('client_notified', contract.id)
              const act = getAction('client_notified', contract.id)
              const windowMs = (contract.notification_window_hours || 2) * 3600 * 1000
              const dueAt = new Date(incident.date_occurred).getTime() + windowMs
              const remaining = Math.max(0, Math.floor((dueAt - Date.now()) / 1000))
              const overdue = !done && Date.now() > dueAt

              let requiredItems: string[] = []
              try {
                if (contract.required_info) requiredItems = JSON.parse(contract.required_info)
              } catch {}

              return (
                <div key={contract.id} className="rounded-lg p-4 space-y-3"
                  style={{ background: done ? 'rgba(0,135,90,0.05)' : 'var(--bg)', border: `1px solid ${done ? '#00875A40' : 'var(--border)'}` }}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-sm" style={{ color: done ? '#00875A' : 'var(--text)' }}>
                        {done && <CheckCircle size={14} className="inline mr-1.5" />}
                        {contract.client_name}
                      </div>
                      {contract.safety_contact_name && (
                        <div className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                          Contact: {contract.safety_contact_name}
                          {contract.safety_contact_email && <> · <a href={`mailto:${contract.safety_contact_email}`} style={{ color: 'var(--accent)' }}>{contract.safety_contact_email}</a></>}
                          {contract.safety_contact_phone && <> · <a href={`tel:${contract.safety_contact_phone}`} style={{ color: 'var(--accent)' }}>{contract.safety_contact_phone}</a></>}
                        </div>
                      )}
                    </div>
                    {!done && (
                      <div className="text-right flex-shrink-0">
                        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Notify within {contract.notification_window_hours}h</div>
                        <div className="text-sm font-bold" style={{ color: overdue ? 'var(--red)' : timerColour(remaining, contract.notification_window_hours * 3600) }}>
                          {overdue ? 'OVERDUE' : formatCountdown(remaining)}
                        </div>
                      </div>
                    )}
                  </div>

                  {requiredItems.length > 0 && !done && (
                    <div className="text-xs space-y-0.5" style={{ color: 'var(--text-muted)' }}>
                      <div className="font-semibold">Required:</div>
                      {requiredItems.map((item, i) => <div key={i}>• {item}</div>)}
                    </div>
                  )}

                  {done && act && (
                    <div className="text-xs" style={{ color: '#00875A' }}>
                      Notified by {act.completed_by_name} at {new Date(act.created_at).toLocaleString('en-AU')}
                    </div>
                  )}

                  {!done && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => generateClientNotice(contract.id)}
                        className="btn btn-secondary text-xs flex items-center gap-1.5"
                      >
                        <FileText size={13} /> Generate Client Email (AI)
                      </button>
                      <button
                        type="button"
                        onClick={() => logAction('client_notified', contract.id, 'manual', `${contract.client_name} notified`)}
                        className="btn text-xs"
                        style={{ background: '#00875A', color: 'white' }}
                      >
                        Mark Notified
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </Section>
      )}

      {/* ── Section 5: Workers Compensation ── */}
      {incident.injury_type && (
        <Section
          title="Workers Compensation"
          icon={<HardHat size={18} />}
          complete={progress.wc?.done === 3}
          defaultOpen={!isDone('wc_insurer')}
        >
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm p-3 rounded-lg"
              style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <div>
                <div className="font-semibold" style={{ color: 'var(--text)' }}>{wcInsurer.name}</div>
                <a href={`tel:${wcInsurer.phone.replace(/\s/g, '')}`}
                  className="font-bold" style={{ color: 'var(--accent)' }}>
                  <Phone size={13} className="inline mr-1" />{wcInsurer.phone}
                </a>
              </div>
            </div>
            {[
              { key: 'wc_insurer', label: 'Insurer notified', actionType: 'wc_insurer' },
              { key: 'wc_claim', label: 'Workers compensation claim lodged', actionType: 'wc_claim' },
              { key: 'worker_informed', label: 'Worker informed of their rights', actionType: 'worker_informed' },
            ].map(item => {
              const done = isDone(item.actionType)
              const act = getAction(item.actionType)
              return (
                <label key={item.key} className="flex items-start gap-3 cursor-pointer">
                  <div
                    className="flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5"
                    style={{ borderColor: done ? '#00875A' : 'var(--border)', background: done ? '#00875A' : 'white', cursor: 'pointer' }}
                    onClick={() => !done && logAction(item.actionType)}
                  >
                    {done && <Check size={12} color="white" />}
                  </div>
                  <div>
                    <div className="text-sm font-medium" style={{ color: done ? '#00875A' : 'var(--text)' }}>{item.label}</div>
                    {done && act && (
                      <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                        {act.completed_by_name} — {new Date(act.created_at).toLocaleString('en-AU')}
                      </div>
                    )}
                  </div>
                </label>
              )
            })}
          </div>
        </Section>
      )}

      {/* ── Section 6: Union / HSR Notification ── */}
      {(() => {
        const hsrContacts = complianceData.contacts.filter(c =>
          c.role?.toLowerCase().includes('hsr') || c.role?.toLowerCase().includes('union')
        )
        if (hsrContacts.length === 0) return null
        return (
          <Section
            title="Union / HSR Notification"
            icon={<Users size={18} />}
            complete={isDone('hsr_notified')}
            defaultOpen={!isDone('hsr_notified')}
          >
            <div className="space-y-3">
              {hsrContacts.map(contact => {
                const done = isDone('hsr_notified', contact.id)
                const act = getAction('hsr_notified', contact.id)
                return (
                  <div key={contact.id} className="flex items-start justify-between gap-3 p-3 rounded-lg"
                    style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                    <div>
                      <div className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{contact.name}</div>
                      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{contact.role}</div>
                      {done && act && (
                        <div className="text-xs mt-1" style={{ color: '#00875A' }}>
                          Notified by {act.completed_by_name} at {new Date(act.created_at).toLocaleString('en-AU')}
                        </div>
                      )}
                    </div>
                    {!done && (
                      <button
                        type="button"
                        onClick={() => logAction('hsr_notified', contact.id, 'manual', `${contact.name} (HSR/Union) notified`)}
                        className="btn btn-secondary text-xs flex-shrink-0"
                      >
                        Mark Notified
                      </button>
                    )}
                    {done && <CheckCircle size={18} style={{ color: '#00875A', flexShrink: 0 }} />}
                  </div>
                )
              })}
            </div>
          </Section>
        )
      })()}

      {/* ── Section 7: Investigation Requirement ── */}
      <Section
        title="Investigation Requirement"
        icon={<Scale size={18} />}
        complete={!!complianceData.investigation}
        defaultOpen={!complianceData.investigation}
      >
        <div className="space-y-3">
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Notifiable incidents require a formal investigation under s.46 of the WHS Act 2011.
          </p>
          {complianceData.investigation ? (
            <div className="flex items-center justify-between p-3 rounded-lg"
              style={{ background: 'rgba(0,135,90,0.06)', border: '1px solid #00875A40' }}>
              <div>
                <div className="text-sm font-semibold" style={{ color: '#00875A' }}>
                  <CheckCircle size={14} className="inline mr-1.5" />
                  {complianceData.investigation.investigation_number} — {complianceData.investigation.title}
                </div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  Status: {complianceData.investigation.status}
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate(`/investigations/${complianceData.investigation.id}`)}
                className="btn btn-secondary text-xs flex items-center gap-1.5"
              >
                <ExternalLink size={13} /> Open
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => navigate(`/investigations/new?incident=${incident.id}`)}
              className="btn btn-primary text-sm flex items-center gap-2"
            >
              <Scale size={14} /> Open Investigation Now
            </button>
          )}
        </div>
      </Section>

      {/* ── Section 8: Compliance Activity Log ── */}
      <Section
        title="Compliance Activity Log"
        icon={<Activity size={18} />}
        defaultOpen={complianceData.actions.length > 0}
      >
        <div className="space-y-2">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={downloadComplianceReport}
              className="btn btn-secondary text-xs flex items-center gap-1.5"
            >
              <Download size={13} /> Download Report
            </button>
          </div>

          {complianceData.actions.length === 0 ? (
            <div className="text-sm text-center py-4" style={{ color: 'var(--text-muted)' }}>
              No compliance actions recorded yet
            </div>
          ) : (
            <div className="space-y-1">
              {[...complianceData.actions].reverse().map(action => (
                <div key={action.id} className="text-xs py-2 flex gap-3"
                  style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                  <span className="flex-shrink-0 font-mono" style={{ color: 'var(--text-muted)' }}>
                    {new Date(action.created_at).toLocaleString('en-AU')}
                  </span>
                  <span className="flex-1">
                    <span className="font-medium" style={{ color: 'var(--text)' }}>
                      {action.action_type.replace(/_/g, ' ')}
                    </span>
                    {action.contact_type && ` — ${action.contact_type}`}
                    {action.method && ` (${action.method})`}
                    {action.notes && ` — ${action.notes}`}
                  </span>
                  <span className="flex-shrink-0 font-medium" style={{ color: 'var(--accent)' }}>
                    {action.completed_by_name}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* ── Written Notice Modal ── */}
      {noticeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border)' }}>
              <div>
                <h3 className="font-bold" style={{ color: 'var(--text)' }}>
                  s38 Written Notice — {regulator.name}
                </h3>
                <div className="text-xs ai-label mt-0.5">AI-assisted draft — review and edit before submitting</div>
              </div>
              <button type="button" onClick={() => setNoticeModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <textarea
                value={noticeDraft}
                onChange={e => setNoticeDraft(e.target.value)}
                rows={24}
                className="w-full font-mono text-xs"
                style={{ resize: 'vertical', lineHeight: 1.6 }}
              />
            </div>
            <div className="flex items-center gap-2 p-4 border-t flex-wrap" style={{ borderColor: 'var(--border)' }}>
              <button
                type="button"
                onClick={async () => {
                  await navigator.clipboard.writeText(noticeDraft)
                  setCopiedNotice(true)
                  setTimeout(() => setCopiedNotice(false), 2000)
                }}
                className="btn btn-secondary text-sm flex items-center gap-1.5"
              >
                <Copy size={14} /> {copiedNotice ? 'Copied!' : 'Copy'}
              </button>
              <button type="button" onClick={downloadNotice}
                className="btn btn-secondary text-sm flex items-center gap-1.5">
                <Download size={14} /> Download .txt
              </button>
              <a href={regulator.url} target="_blank" rel="noopener noreferrer"
                className="btn btn-secondary text-sm flex items-center gap-1.5"
                style={{ textDecoration: 'none' }}>
                <ExternalLink size={14} /> Open Portal
              </a>
              <button
                type="button"
                onClick={() => {
                  logAction('written_notification', undefined, 'portal', `Written notice submitted to ${regulator.name}`)
                  setNoticeModal(false)
                }}
                className="btn btn-danger text-sm flex items-center gap-1.5 ml-auto"
              >
                <Check size={14} /> Mark Submitted
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Client Notice Modal ── */}
      {clientNoticeModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border)' }}>
              <div>
                <h3 className="font-bold" style={{ color: 'var(--text)' }}>Client Notification Email</h3>
                <div className="text-xs ai-label mt-0.5">AI-assisted draft — external-facing, factual only</div>
              </div>
              <button type="button" onClick={() => setClientNoticeModal({ open: false })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {clientNoticeLoading ? (
                <div className="text-center py-12 text-sm animate-pulse" style={{ color: 'var(--text-muted)' }}>
                  Generating client notification...
                </div>
              ) : (
                <textarea
                  value={clientNoticeModal.draft || ''}
                  onChange={e => setClientNoticeModal(prev => ({ ...prev, draft: e.target.value }))}
                  rows={20}
                  className="w-full text-sm"
                  style={{ resize: 'vertical', lineHeight: 1.7 }}
                />
              )}
            </div>
            {!clientNoticeLoading && clientNoticeModal.draft && (
              <div className="flex items-center gap-2 p-4 border-t flex-wrap" style={{ borderColor: 'var(--border)' }}>
                <button
                  type="button"
                  onClick={async () => {
                    await navigator.clipboard.writeText(clientNoticeModal.draft || '')
                    setCopiedClient(true)
                    setTimeout(() => setCopiedClient(false), 2000)
                  }}
                  className="btn btn-secondary text-sm flex items-center gap-1.5"
                >
                  <Copy size={14} /> {copiedClient ? 'Copied!' : 'Copy'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (clientNoticeModal.contractId) {
                      const contract = complianceData.contracts.find(c => c.id === clientNoticeModal.contractId)
                      logAction('client_notified', clientNoticeModal.contractId, 'email', `${contract?.client_name || 'Client'} notified via email`)
                    }
                    setClientNoticeModal({ open: false })
                  }}
                  className="btn text-sm flex items-center gap-1.5 ml-auto"
                  style={{ background: '#00875A', color: 'white' }}
                >
                  <Check size={14} /> Mark Notified
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
